import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';

import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Row,
  Col,
  Typography,
  Switch,
  Space,
  List,
  Divider,
  Button,
  InputNumber,
  Tooltip,
  Spin,
} from 'antd';
import debounce from 'lodash.debounce';

import { SUBSCRIPTION_STATUS } from '../../constants';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { t } from '../../utils/i18n';
import createQueryConfig from '../../queries';
import useMerchantSettings from '../../hooks/use-merchant-settings';
import { useChargebeePortal } from '@shared/hooks/use-chargebee-portal';

const containerStyle = {
  display: 'flex',
  height: '100%',
  flex: '1 1 0%',
};

const containerContentStyle = {
  flex: '1 1 0%',
};

const headerContainerStyle = {
  alignItems: 'center',
  display: 'flex',
  margin: 'auto',
  marginBottom: '1.6rem',
  minHeight: '94px',
  padding: '1.6rem 2.4rem 0',
  width: '100%',
};

const overlayStyle = {
  width: '100%',
  display: 'flex',
  minHeight: '100vh',
};

const headerTitleStyle = {
  fontSize: '25px',
  fontWeight: 700,
  margin: 0,
  padding: 0,
  flex: 1,
};

const portalSessionSpinStyle = (isLoading) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1000,
  display: isLoading ? 'block' : 'none',
});

function Header() {
  return (
    <header style={headerContainerStyle} slot="header">
      <h1 style={headerTitleStyle}>{t('company-settings.title')}</h1>
    </header>
  );
}

function Navigation() {
  return (
    <div slot="navigation">
      <shore-navigation>
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.basic-information')}
          url="/settings/basic-information"
        />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.booking-hours')}
          url="/settings/booking-hours"
        />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.closed-dates-and-absences')}
          url="/settings/closing-and-absences"
        />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.company-profile')}
          url="/settings/company-profile"
        />
        <shore-navigation-item data-url title="Subscription" isActive />
        <shore-navigation-item
          data-url
          title={t('company-settings.nav.privacy-security')}
          url="/settings/privacy"
        />
      </shore-navigation>
    </div>
  );
}

const settingsContainerStyle = {
  height: '100%',
  position: 'relative',
  overflowY: 'auto',
};

function SMSLimitForm({ merchantId, value, updateSmsLimit }) {
  const [smsLimit, setSmsLimit] = useState(value || 0);

  function handleOnInputLimitChange(newSmsLimit) {
    const merchant = { id: merchantId };
    setSmsLimit(newSmsLimit);

    updateSmsLimit({
      merchant,
      limit_type: 'BY_LIMIT',
      additional_value: newSmsLimit,
    });
  }

  return (
    <Space direction="vertical">
      <Typography.Text>
        {t('company-settings.subscription.sms-settings.sms-limit-input')}
      </Typography.Text>
      <InputNumber
        value={smsLimit}
        min={0}
        style={{ width: 200 }}
        onChange={handleOnInputLimitChange}
      />
    </Space>
  );
}

function SMSSubscriptionOverviewList({
  locale,
  currencyCode,
  limitType,
  freeSmsCount = 0,
  additionalSmsCount = 0,
  sentSmsCount = 0,
  pricePerSmsInCents,
}) {
  const isSmsLimitTypeUnlimited = limitType === 'UNLIMITED';
  const totalSmsQuota = (freeSmsCount || 0) + (additionalSmsCount || 0);
  const pricePerSms = pricePerSmsInCents / 100;
  const totalSmsDueForPayment = Math.max(
    (sentSmsCount || 0) - (freeSmsCount || 0),
    0
  );
  const totalSmsCost = pricePerSms * totalSmsDueForPayment;

  return (
    <List itemLayout="horizontal" style={{ maxWidth: 500 }}>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.sms-quota'
          )}
        </Typography.Text>
        <Typography.Text>
          {isSmsLimitTypeUnlimited
            ? t(
                'company-settings.subscription.sms-settings.sms-limit-type-unlimited'
              )
            : totalSmsQuota}
        </Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.free-sms'
          )}
        </Typography.Text>
        <Typography.Text>{freeSmsCount}</Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.total-sms-sent'
          )}
        </Typography.Text>
        <Typography.Text>{sentSmsCount}</Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.cost-per-sms'
          )}
        </Typography.Text>
        <Typography.Text>
          {new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          }).format(pricePerSms)}
        </Typography.Text>
      </List.Item>
      <List.Item style={{ padding: '0.8rem 0rem' }}>
        <Typography.Text strong>
          {t(
            'company-settings.subscription.sms-settings.sms-overview.total-sms-cost'
          )}
        </Typography.Text>
        <Typography.Text>
          {new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
          }).format(totalSmsCost)}
        </Typography.Text>
      </List.Item>
    </List>
  );
}

function SMS({ merchantId }) {
  const { queryClient, apiClient } = useGlobals();
  const { locale } = useSelector((store) => store.currentAccount);
  const { currency } = useSelector((store) => store.currentMerchant);
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const {
    data: smsUsage,
    status: smsUsageStatus,
    refetch: refetchUsage,
  } = useQuery(queryConfig.smsCurrentMonthUsage(merchantId));
  const { status: subscriptionStatus, data: subscription } = useQuery(
    queryConfig.subscription({ merchantId })
  );

  const hasError = [subscriptionStatus, smsUsageStatus].includes('error');
  const isLoading = [subscriptionStatus, smsUsageStatus].includes('pending');

  const [isSmsFeatureEnabled, setIsSmsFeatureEnabled] = useState(false);
  const [isSmsLimitVisible, setSmsLimitVisibility] = useState(false);

  const merchant = { id: merchantId };
  const isTogglingRef = useRef(false);

  async function updateSmsLimit(payload) {
    await apiClient.smsUsage.updateLimit(payload).then(refetchUsage);
  }

  const debouncedUpdateSmsLimit = debounce(updateSmsLimit, 1000);

  function handleOnSetSMSLimitChange(checkedValue) {
    setSmsLimitVisibility(checkedValue);
  }

  async function handleOnSMSFeatureChange(checkedValue) {
    const payload = isSmsFeatureEnabled
      ? { limit_type: 'BY_LIMIT', merchant, additional_value: 0 }
      : { limit_type: 'UNLIMITED', merchant };

    isTogglingRef.current = true;
    setIsSmsFeatureEnabled(checkedValue);
    try {
      await updateSmsLimit(payload);
    } catch {
      setIsSmsFeatureEnabled(!checkedValue);
    } finally {
      isTogglingRef.current = false;
    }
  }

  useEffect(() => {
    if (smsUsage) {
      const isFeatureActive =
        smsUsage.limit_type === 'UNLIMITED' || smsUsage.additional_value > 0;
      setIsSmsFeatureEnabled(isFeatureActive);
      setSmsLimitVisibility(
        isFeatureActive && smsUsage.limit_type !== 'UNLIMITED'
      );
    }
  }, [smsUsage]);

  useEffect(() => {
    if (!isSmsFeatureEnabled) {
      setSmsLimitVisibility(false);
    }
  }, [isSmsFeatureEnabled]);

  useEffect(() => {
    if (
      !isTogglingRef.current &&
      isSmsFeatureEnabled &&
      !isSmsLimitVisible &&
      smsUsage?.limit_type === 'BY_LIMIT'
    ) {
      updateSmsLimit({ limit_type: 'UNLIMITED', merchant });
    }
  }, [isSmsFeatureEnabled, isSmsLimitVisible, smsUsage]);

  if (isLoading || hasError) {
    return null;
  }

  return (
    <Col span={24}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        {t('company-settings.subscription.sms-settings.title')}
      </Typography.Title>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', maxWidth: 650 }}
      >
        {t('company-settings.subscription.sms-settings.description')}
      </Typography.Text>
      <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
        <Col span={24}>
          <Space size="small" direction="horizontal" align="center">
            <Switch
              checked={isSmsFeatureEnabled}
              onChange={handleOnSMSFeatureChange}
            />
            <Typography.Text>
              {t(
                'company-settings.subscription.sms-settings.activate-unlimited-sms'
              )}
            </Typography.Text>
          </Space>
        </Col>
        <Col span={24}>
          <Space size="small" direction="horizontal" align="center">
            <Switch
              checked={isSmsLimitVisible}
              onChange={handleOnSetSMSLimitChange}
              disabled={!isSmsFeatureEnabled}
            />
            <Typography.Text {...(!isSmsFeatureEnabled && { disabled: true })}>
              {t(
                'company-settings.subscription.sms-settings.activate-sms-limit'
              )}
            </Typography.Text>
          </Space>
        </Col>
      </Row>
      {isSmsLimitVisible && (
        <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
          <Col span={24}>
            <SMSLimitForm
              merchantId={merchantId}
              value={smsUsage.additional_value}
              updateSmsLimit={debouncedUpdateSmsLimit}
            />
          </Col>
        </Row>
      )}
      <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
        <Col span={24}>
          <Typography.Title level={5}>
            {t('company-settings.subscription.sms-settings.sms-overview.title')}{' '}
            <Tooltip
              title={t(
                'company-settings.subscription.sms-settings.sms-overview.title-tooltip'
              )}
            >
              <QuestionCircleOutlined style={{ color: '#bdc3c7' }} />
            </Tooltip>
          </Typography.Title>
          <SMSSubscriptionOverviewList
            locale={locale}
            currencyCode={currency}
            limitType={smsUsage.limit_type}
            sentSmsCount={smsUsage.sms_sent}
            freeSmsCount={subscription.sms ? subscription.sms.sms_limit : 0}
            additionalSmsCount={smsUsage.additional_value}
            pricePerSmsInCents={
              subscription.sms ? subscription.sms.price_per_sms_in_cents : 0
            }
          />
        </Col>
      </Row>
    </Col>
  );
}

function SubscriptionPortalSession({ closePortal, _key }) {
  const { isLoading: isLoadingMerchantSettings, merchantSettings } =
    useMerchantSettings();
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { status: portalSessionsStatus, data } = useQuery(
    queryConfig.subscriptionPortalSession(merchantSettings)
  );

  const { chargebeeInstance, hasError: chargebeeInitError } =
    useChargebeePortal();

  const isLoading =
    isLoadingMerchantSettings || portalSessionsStatus === 'pending';

  const hasPortalSessionError =
    portalSessionsStatus === 'error' ||
    chargebeeInitError ||
    (portalSessionsStatus === 'success' && !chargebeeInstance);

  const canOpenPortal =
    portalSessionsStatus === 'success' &&
    data &&
    chargebeeInstance &&
    !chargebeeInitError;

  useEffect(() => {
    if (canOpenPortal) {
      chargebeeInstance.setPortalSession(() => Promise.resolve(data));
      chargebeeInstance.createChargebeePortal().open({
        closeOnEscape: true,
        showCloseButton: true,
        close: closePortal,
      });
    }
  }, [canOpenPortal, closePortal, data]);

  if (isLoading) {
    return (
      <Spin
        data-testid="portal-session-loading-spinner"
        style={portalSessionSpinStyle(true)}
      />
    );
  }

  if (hasPortalSessionError) {
    return (
      <p>
        {t(
          'company-settings.subscription.manage-subscription.portal-session-error'
        )}
      </p>
    );
  }

  return null;
}

function SubscriptionManagement() {
  const [portalSessionAttempt, setPortalSessionAttempt] = useState(0);

  function handleOnClosePortalClick() {
    setPortalSessionAttempt(0);
  }

  function handleOnOpenPortalClick() {
    setPortalSessionAttempt((prev) => prev + 1);
  }

  const isPortalSessionVisible = portalSessionAttempt > 0;

  return (
    <Col span={24}>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        {t('company-settings.subscription.manage-subscription.title')}
      </Typography.Title>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', maxWidth: 650 }}
      >
        {t('company-settings.subscription.manage-subscription.description')}
      </Typography.Text>
      <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
        <Col span={24}>
          <Button type="primary" size="large" onClick={handleOnOpenPortalClick}>
            {t('button.manage-subscription.title')}
          </Button>
        </Col>
      </Row>
      {isPortalSessionVisible && (
        <SubscriptionPortalSession
          key={portalSessionAttempt}
          closePortal={handleOnClosePortalClick}
        />
      )}
    </Col>
  );
}

function Settings({ merchantId }) {
  return (
    <div slot="content" style={settingsContainerStyle}>
      <Row>
        <SubscriptionManagement />
        <Divider />
        <SMS merchantId={merchantId} />
      </Row>
    </div>
  );
}

export function SubscriptionSettings() {
  const { isLoading: isLoadingMerchantSettings, merchantSettings } =
    useMerchantSettings();
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { status: subscriptionQueryStatus, data: subscription } = useQuery(
    queryConfig.subscription(merchantSettings)
  );
  const hasChargebeeSubscription = subscriptionQueryStatus === 'success';
  const subscriptionPartner = subscription?.customer?.partner;

  if (isLoadingMerchantSettings || subscriptionQueryStatus === 'pending') {
    return null;
  }

  if (!hasChargebeeSubscription) {
    return <Navigate to="/settings/contract" />;
  }

  if (subscription.status === SUBSCRIPTION_STATUS.IN_TRIAL) {
    return <Navigate to="/subscription" />;
  }

  if (
    subscriptionPartner?.toLowerCase() === 'herold' ||
    subscriptionPartner?.toLowerCase() === 'kreativmedia'
  ) {
    return <Navigate to="/settings" />;
  }

  return (
    <div style={containerStyle}>
      <div style={containerContentStyle}>
        <div>
          <shore-overlay-layout style={overlayStyle} withoutPadding showContent>
            <Header />
            <Navigation />
            <Settings merchantId={merchantSettings.merchantId} />
          </shore-overlay-layout>
        </div>
      </div>
    </div>
  );
}
